import { clamp } from '../math.ts';
import type { DebugDefaultsCamera } from './types.ts';

/** Same keys as the play-camera policy. HUD and migrate both read this. */
export type PlayCameraSettings = DebugDefaultsCamera;

export type PlayCameraSliderKey = Exclude<keyof PlayCameraSettings, 'enabled'>;

export type PlayCameraSliderSpec = {
  label: string;
  hint: string;
  min: number;
  max: number;
  step: number;
  format: (value: number) => string;
};

export type PlayCameraLimit = 'Speed' | 'Fit' | 'Min' | 'Max';

/** Aim-bias lerp may overshoot the dest; slider and look-ahead share this cap. */
export const AIM_BIAS_MAX = 1.5;

/**
 * Shipped camera starts. Slider min/max for auto zoom match `PLAY_MIN_ZOOM` / `MAX_ZOOM`
 * in `src/game/view.ts` (0.1 / 4). Keep those literals in lockstep.
 */
export const DEFAULT_PLAY_CAMERA: PlayCameraSettings = {
  enabled: true,
  placeZoom: 1,
  restZoom: 0.75,
  fastZoom: 0.55,
  speedSensitivity: 1.4,
  lookAhead: 0.5,
  aimBias: 0.18,
  paddingM: 4,
  circleFit: 0.35,
  fitBlend: 0.4,
  smoothness: 8,
  smoothnessIn: 4.5,
  minAutoZoom: 0.25,
  maxAutoZoom: 2,
};

export function formatZoom(value: number): string {
  return value.toFixed(2);
}

export function formatUserZoomFactor(value: number): string {
  return `×${value.toFixed(2)}`;
}

export function formatPlayCameraLimit(limit: PlayCameraLimit): string {
  return limit === 'Speed' ? 'Speed z' : limit;
}

export const AUTO_CAMERA_HINT =
  'When on, the camera looks ahead toward the planning circle and zooms with boat speed. Wheel or pinch zoom stays as the User multiplier on top.';

export const CAMERA_READOUT_HINTS = {
  fit: 'Closest zoom that still frames the hull and the chosen slice of the planning circle. Fit is a soft pull toward that zoom, not a hard ceiling.',
  speed: 'Zoom from Rest, Fast, and Sensitivity at the current boat speed, scaled so a 1280×720 window and a larger one show the same metres. Before Fit blend and Min/Max.',
  auto: 'Zoom auto camera wants before your wheel or pinch factor: the speed curve blended toward Fit, then clamped to Min/Max.',
  limit: 'Which term last moved Auto: Speed z (the speed curve), Fit, or the Min/Max band.',
  user: 'Wheel or pinch zoom as a multiplier on Auto. ×1.00 means you have not overridden it.',
  view: 'Zoom actually on the camera right now, after smoothing.',
} as const;

export const PLAY_CAMERA_SLIDERS: {
  readonly [K in PlayCameraSliderKey]: PlayCameraSliderSpec;
} = {
  placeZoom: {
    label: 'Place zoom',
    hint: 'Zoom while placing the hull, before you plant and pick a heading. This is the Zoom readout. Higher is closer.',
    min: 0.5,
    max: 2,
    step: 0.01,
    format: formatZoom,
  },
  restZoom: {
    label: 'Rest zoom',
    hint: 'Zoom after the boat is planted and whenever speed is 0. This is the Zoom readout. Higher is closer. Speed then pulls toward Fast zoom.',
    min: 0.5,
    max: 2,
    step: 0.01,
    format: formatZoom,
  },
  fastZoom: {
    label: 'Fast zoom',
    hint: 'How far the camera pulls back at full speed when Sensitivity is ×1.00. Lower is more zoomed out. This is the speed-curve destination, not a separate mode. Min zoom can still stop it.',
    min: 0.25,
    max: 1.5,
    step: 0.01,
    format: formatZoom,
  },
  speedSensitivity: {
    label: 'Sensitivity',
    hint: 'How hard boat speed pulls from Rest toward (or past) Fast zoom. ×1.00 reaches Fast zoom at full speed. ×2.00 doubles that pull-back at the same speed.',
    min: 0.25,
    max: 4,
    step: 0.05,
    format: formatUserZoomFactor,
  },
  lookAhead: {
    label: 'Look-ahead',
    hint: 'How far the view sits toward the circle centre at full speed. 0 at rest. 1 centres on the circle when the boat is at reference speed. Does not change Fit zoom.',
    min: 0,
    max: 1,
    step: 0.01,
    format: (value) => value.toFixed(2),
  },
  aimBias: {
    label: 'Aim bias',
    hint: 'How far Auto eases toward the movement dot. 0 ignores the aim; 1 lands on it; above 1 overshoots past the dest. Keep this small or the view chases the cursor. Pan only — does not change Fit zoom.',
    min: 0,
    max: AIM_BIAS_MAX,
    step: 0.01,
    format: (value) => value.toFixed(2),
  },
  paddingM: {
    label: 'Padding',
    hint: 'Extra metres kept around the hull and the framed part of the planning circle. Higher lets Fit pull farther out.',
    min: 0,
    max: 40,
    step: 0.5,
    format: (value) => `${value.toFixed(1)} m`,
  },
  circleFit: {
    label: 'Circle fit',
    hint: 'How much of the planning circle Fit measures. 0 is hull and circle centre only; 1 is the whole disk. Fit is a blend, so this no longer pins Rest/Fast.',
    min: 0,
    max: 1,
    step: 0.01,
    format: (value) => value.toFixed(2),
  },
  fitBlend: {
    label: 'Fit blend',
    hint: 'How hard a clipping disk pulls Auto toward Fit. 0 is the speed curve only. 1 is the old hard ceiling (min of Fit and speed). Rest/Fast/Sensitivity always move Auto.',
    min: 0,
    max: 1,
    step: 0.01,
    format: (value) => value.toFixed(2),
  },
  smoothness: {
    label: 'Smoothness',
    hint: 'How quickly zoom and look-ahead catch up when pulling back after a sprint. Higher feels snappier; lower eases more.',
    min: 0.5,
    max: 24,
    step: 0.5,
    format: (value) => value.toFixed(1),
  },
  smoothnessIn: {
    label: 'Smoothness in',
    hint: 'How quickly zoom catches up when closing in after a slow-down. Lower than Smoothness so zoom-in is softer than zoom-out.',
    min: 0.5,
    max: 24,
    step: 0.5,
    format: (value) => value.toFixed(1),
  },
  minAutoZoom: {
    label: 'Min zoom',
    hint: 'Floor for auto zoom, in 1280×720 units. The camera will not pull back farther than this, even if Fast, Fit, or Sensitivity ask for it. The Limit readout says Min when this floor wins.',
    min: 0.1,
    max: 2,
    step: 0.01,
    format: formatZoom,
  },
  maxAutoZoom: {
    label: 'Max zoom',
    hint: 'Ceiling for auto zoom, in 1280×720 units. The camera will not zoom in closer than this at rest.',
    min: 0.5,
    max: 4,
    step: 0.01,
    format: formatZoom,
  },
};

export function patchPlayCamera(
  settings: PlayCameraSettings,
  key: PlayCameraSliderKey,
  value: number,
): PlayCameraSettings {
  const spec = PLAY_CAMERA_SLIDERS[key];
  const next = { ...settings, [key]: clamp(value, spec.min, spec.max) };

  if (key === 'minAutoZoom' && next.minAutoZoom > next.maxAutoZoom) {
    next.maxAutoZoom = next.minAutoZoom;
  }

  if (key === 'maxAutoZoom' && next.maxAutoZoom < next.minAutoZoom) {
    next.minAutoZoom = next.maxAutoZoom;
  }

  return next;
}
